/* eslint no-restricted-syntax: 0 */
/* eslint no-await-in-loop: 0 */
/* eslint no-else-return: 0 */
import * as tfcore from '@tensorflow/tfjs-core';
import * as tf from '@tensorflow/tfjs';
import * as mpHands from '@mediapipe/hands';
import * as handdetection from '@tensorflow-models/hand-pose-detection';
import * as tfjsWasm from '@tensorflow/tfjs-backend-wasm';
import * as seedrandom from 'seedrandom';
import {
  setBackendAndEnvFlags,
  normalizePlain,
  flatOneHot,
  fisherYates,
  convertToTfDataset,
  convertBase64ToObject,
  convertObjectToBase64,
  uint8ToString,
} from '@utils/hooks/machineLearning/util'
import { STATE, MODEL_TYPES, MODEL_BACKEND_MAP, MEDIAPIPE_HANDS_CONFIG, BACKEND_FLAGS_MAP } from '../params';
import { Camera } from '../camera';
import { getImageFromBase64 } from '../util';

tfjsWasm.setWasmPaths(`https://cdn.jsdelivr.net/npm/@tensorflow/tfjs-backend-wasm@${tfjsWasm.version_wasm}/dist/`);


export default class HandsHandler {
  modelType = MODEL_TYPES.HANDS;

  state = STATE;

  seed = seedrandom('testSuite');

  minSamplesRequired = {
    samples: 2,
    classes: 2,
  };

  trainingParams = {
    denseUnits: 100,
    epochs: 50,
    fineTunningEpochs: -1,
    learningRate: 0.1,
    batchSize: 16,
  };

  wrapper = null;

  detector = null;

  camera = null;

  rafId = null;

  hands = null;

  classifying = false;

  predictionCallback = null;

  model = null;

  samples = {
    class0: {
      index: 0,
      name: '',
      image: [],
      active: true,
      tensors: [],
    },
  };

  trainDataset = null;

  validationDataset = null;

  classNamesToIndex = {};

  constructor(wrapper, predictionCallback) {
    this.wrapper = wrapper;
    this.predictionCallback = predictionCallback;
  }

  init = async () => {
    await tf.ready();
    await this.initDefaultValueMap();
    await setBackendAndEnvFlags(this.state.flags, this.state.backend);
    this.renderPrediction();
    if (!this.detector) {
      this.detector = await this.createDetector();
    }
  }

  initDefaultValueMap = async () => {
    const newState = { ...STATE };

    newState.model = handdetection.SupportedModels.MediaPipeHands;
    const backends = MODEL_BACKEND_MAP[newState.model];
    newState.backend = backends[0];
    newState.modelConfig = MEDIAPIPE_HANDS_CONFIG;
    newState.modelConfig.type = 'full';
    newState.modelConfig.maxNumHands = 1;

    // Clean up the cache to query tunable flags' default values.
    const TUNABLE_FLAG_DEFAULT_VALUE_MAP = {};
    newState.flags = {};
    for (let index = 0; index < BACKEND_FLAGS_MAP[newState.backend].length; index += 1) {
      const flag = BACKEND_FLAGS_MAP[newState.backend][index];
      TUNABLE_FLAG_DEFAULT_VALUE_MAP[flag] = await tfcore.env().getAsync(flag);
    }

    // Initialize newState.flags with tunable flags' default values.
    for (const flag in TUNABLE_FLAG_DEFAULT_VALUE_MAP) {
      if (BACKEND_FLAGS_MAP[newState.backend].indexOf(flag) > -1) {
        newState.flags[flag] = TUNABLE_FLAG_DEFAULT_VALUE_MAP[flag];
      }
    }

    this.state = newState;
  }

  createDetector = () => {
    const state = this.state;
    const runtime = state.backend.split('-')[0];
    switch (state.model) {
      default:
      case handdetection.SupportedModels.MediaPipeHands:
        if (runtime === 'mediapipe') {
          return handdetection.createDetector(state.model, {
            runtime,
            modelType: state.modelConfig.type,
            maxHands: state.modelConfig.maxNumHands,
            solutionPath: `https://cdn.jsdelivr.net/npm/@mediapipe/hands@${mpHands.VERSION}`,
          });
        } else if (runtime === 'tfjs') {
          return handdetection.createDetector(state.model, {
            runtime,
            modelType: state.modelConfig.type,
            maxHands: state.modelConfig.maxNumHands,
          });
        }
    }
    return null;
  }

  renderPrediction = async () => {
    await this.renderResult();
    this.rafId = requestAnimationFrame(this.renderPrediction);
    return null;
  }

  renderResult = async () => {
    const camera = this.camera;
    if (camera?.video?.readyState < 2) {
      await new Promise((resolve) => {
        camera.video.onloadeddata = () => {
          resolve(true);
        };
      });
    }

    this.hands = null;

    if (this.detector != null && camera) {
      try {
        this.hands = await this.detector.estimateHands(camera.video, { flipHorizontal: false });
        if (this.classifying) {
          await this.classify();
        }
      } catch (error) {
        this.detector.dispose();
        this.detector = null;
        console.error('================', error);
      }
    }

    camera?.drawCtx();

    if (camera && this.hands && this.hands.length > 0 && this.state.drawEstimation) {
      camera.drawResults(this.hands);
    }
  }

  classify = async () => {
    if (this.model && this.predictionCallback) {
      const results = {};
      if (this.hands.length > 0) {
        const embeddings = tf.tensor([normalizePlain(this.hands[0].keypoints3D)]);
        const logits = this.model.predict(embeddings);
        const values = await logits.data();

        for (let i = 0; i < values.length; i += 1) {
          Object.keys(this.samples).forEach((key) => {
            if (this.samples[key].index === i) {
              results[this.samples[key].name] = parseFloat(values[i].toFixed(4))
            }
          });
        }
        // console.log('==========results', results);
        embeddings.dispose();
        if (Array.isArray(logits)) {
          logits.forEach((tensor) => tensor.dispose())
        } else {
          logits.dispose()
        }
      }
      this.predictionCallback(results);
    }
  }

  startMedia = async () => {
    this.camera = await Camera.setupCamera(this.state.camera, this.wrapper);
    this.renderPrediction();
    if (!this.detector) {
      this.detector = await this.createDetector();
    }
  }

  stopMedia = () => {
    window.cancelAnimationFrame(this.rafId);
    this.rafId = null;

    if (this.detector != null) {
      this.detector.dispose();
      this.detector = null;
    }

    if (this.camera != null) {
      this.camera.video.srcObject.getVideoTracks().forEach((track) => track.stop());
      this.camera.clearCtx();
    }

    if (this.predictionCallback && this.classifying) {
      this.predictionCallback({});
    }
  }

  clearAllSamples = () => {
    this.classifying = false;
    this.samples = {};
    this.model = null;
  }

  startClassifying = () => {
    this.classifying = true;
  }

  stopClassifying = () => {
    this.classifying = false;
  }

  setTrainingParams = (params) => {
    this.trainingParams = {
      ...this.trainingParams,
      ...params,
    };
  }

  getTrainingParams = () => {
    return this.trainingParams;
  }

  getMinSamplesRequired = () => {
    return this.minSamplesRequired;
  }

  addClass = (className = '') => {
    const classIndex = Object.keys(this.samples).length || 0;
    this.samples[`class${classIndex}`] = {
      index: classIndex,
      name: className,
      image: [],
      active: true,
      tensors: [],
    }
    this.updateModelMetadata()
  }

  editClassName = (id, name) => {
    if (this.samples[id]) {
      this.samples[id].name = name
      this.updateModelMetadata()
    } else {
      console.error(`Sample with id ${id} not found.`)
    }
  }

  deleteClass = (id) => {
    if (this.samples[id]) {
      delete this.samples[id];

      let i = 0;
      const newSamples = {};
      Object.keys(this.samples).map((sample) => {
        this.samples[sample].index = i;
        newSamples[`class${i}`] = this.samples[sample];
        i += 1;
        return null;
      });
      this.samples = { ...newSamples }
      this.updateModelMetadata();
    } else {
      console.error(`Sample with id ${id} not found.`)
    }
  }

  deleteSampleImages = (id, indexes) => {
    if (this.samples[id]) {
      this.samples[id].image = this.samples[id].image.filter((_, index) => !indexes.includes(index))
      this.samples[id].tensors = this.samples[id].tensors.filter(
        (_, index) => !indexes.includes(index),
      )
    } else {
      console.error(`Sample with id ${id} not found.`)
    }
  }

  addSample = async (className, image) => {
    if (this.detector && ((this.hands && this.hands.length > 0) || image) && className && this.camera) {
      let firstHand = null
      let normalized = null
      let snapshot = ''
      if (image) {
        try {
          const htmlImage = await getImageFromBase64(image)
          this.hands = await this.detector.estimateHands(htmlImage, { flipHorizontal: false })
          this.camera.canvas.width = htmlImage.width
          this.camera.canvas.height = htmlImage.height
          this.camera.video.srcObject = null
          this.camera.ctx.drawImage(htmlImage, 0, 0)
          this.camera.drawResults(this.hands)

          firstHand = this.hands[0]
          normalized = normalizePlain(firstHand.keypoints3D ?? [])
          snapshot = this.camera.snapshot()

          this.camera.ctx.clearRect(0, 0, htmlImage.width, htmlImage.height)
          this.camera = await Camera.setupCamera(this.state.camera, this.wrapper);
        } catch (_) {
          return 0
        }
      } else {
        firstHand = this.hands[0]
        normalized = normalizePlain(firstHand.keypoints3D ?? [])
        snapshot = this.camera.snapshot()
      }

      this.samples[className] = {
        ...this.samples[className],
        image: [...this.samples[className].image, snapshot],
        tensors: [...this.samples[className].tensors, normalized],
      }

      return this.samples[className].tensors.length || 0
    }
    return 0
  }

  getSamples = () => {
    return { ...this.samples }
  }

  importSamples = (samples) => {
    if (Object.keys(samples).length > 0) {
      this.samples = samples;
    }
  }

  prepare = () => {
    const tensorSamples = []
    Object.keys(this.samples).forEach((className) => {
    // for (const className in this.samples) {
      if (this.samples[className].tensors.length === 0) {
        throw new Error('Add some examples before training');
      }
      const index = this.samples[className].index
      if (!tensorSamples[index]) {
        tensorSamples[index] = []
      }
      tensorSamples[index] = this.samples[className].active ? this.samples[className].tensors : []
    });

    const datasets = convertToTfDataset(tensorSamples)
    this.trainDataset = datasets.trainDataset
    this.validationDataset = datasets.validationDataset
  }

  train = async (callbacks) => {
    await tf.ready();
    this.prepare();
    const classNamesLength = Object.keys(this.samples).length
    const firstSampleKey = Object.keys(this.samples)[0]
    const firstTensor = this.samples[firstSampleKey].tensors[0]
    const inputSize = Array.isArray(firstTensor) ? firstTensor.length : 0

    // const callbacks = {};
    // callbacks.onTrainEnd = (logs) => {
    //   console.log('==========onTrainEnd', logs);
    // };
    // callbacks.onEpochBegin = (epoch, logs) => {
    //   console.log('==========onEpochBegin', epoch, logs);
    // };
    // callbacks.onEpochEnd = (epoch, logs) => {
    //   console.log('==========onEpochEnd', epoch, logs);
    // };

    // in case we need to use a seed for predictable training
    const varianceScaling = tf.initializers.varianceScaling({})

    this.model = tf.sequential({
      layers: [
        // Layer 1.
        tf.layers.dense({
          inputShape: [inputSize],
          units: this.trainingParams.denseUnits || 100,
          activation: 'relu',
          kernelInitializer: varianceScaling, // 'varianceScaling'
          useBias: true,
        }),
        // Layer 2 dropout
        tf.layers.dropout({ rate: 0.5 }),
        // Layer 3. The number of units of the last layer should correspond
        // to the number of classes we want to predict.
        tf.layers.dense({
          units: classNamesLength,
          kernelInitializer: varianceScaling, // 'varianceScaling'
          useBias: false,
          activation: 'softmax',
        }),
      ],
    })

    this.updateModelMetadata();

    const optimizer = tf.train.rmsprop(parseFloat(this.trainingParams.learningRate));
    this.model.compile({
      optimizer,
      loss: 'categoricalCrossentropy',
      metrics: ['accuracy'],
    });

    if (!(parseInt(this.trainingParams.batchSize, 10) > 0)) {
      throw new Error('Batch size is 0 or NaN. Please choose a non-zero fraction');
    }

    const trainData = this.trainDataset.batch(parseInt(this.trainingParams.batchSize, 10));
    const validationData = this.validationDataset.batch(parseInt(this.trainingParams.batchSize, 10));

    await this.model.fitDataset(trainData, {
      epochs: parseInt(this.trainingParams.epochs, 10),
      validationData,
      callbacks,
    });

    optimizer.dispose(); // cleanup

    return this.model;
  }

  save = async (name = 'modelo-educabot-manos') => {
    // Save to local storage
    const saveResults = await this.model.save(`localstorage://${name}`);
    return saveResults;
  }

  saveModelToDB = async () => {
    // Get model JSON and weights data for database storage
    let modelJson = null;
    let modelWeights = null;

    const handlerToSave = async (artifacts) => {
      // artifacts contains all necessary model data including weightSpecs
      modelJson = {
        modelTopology: artifacts.modelTopology,
        weightsManifest: artifacts.weightsManifest,
        weightSpecs: artifacts.weightSpecs, // Include weight specifications
        format: artifacts.format,
        generatedBy: artifacts.generatedBy,
        convertedBy: artifacts.convertedBy,
        userDefinedMetadata: artifacts.userDefinedMetadata,
      };

      modelWeights = artifacts.weightData;

      return {
        modelArtifactsInfo: {
          dateSaved: new Date(),
          modelTopologyType: 'JSON',
          modelTopologyBytes: JSON.stringify(modelJson).length,
          weightSpecsBytes: artifacts.weightSpecs ? JSON.stringify(artifacts.weightSpecs).length : 0,
          weightDataBytes: modelWeights ? modelWeights.byteLength : 0,
        },
      };
    };

    await this.model.save(tf.io.withSaveHandler(handlerToSave));

    return {
      json: JSON.stringify(modelJson),
      weights: modelWeights, // This is an ArrayBuffer
      weightsBase64: modelWeights ? btoa(uint8ToString(new Uint8Array(modelWeights))) : null,
    };
  }

  loadModelFromDB = async (json, weights) => {
    const modelData = JSON.parse(json);
    const weightData = atob(weights);
    const weightBuffer = Uint8Array.from(weightData, c => c.charCodeAt(0)).buffer;

    // Include all necessary model artifacts including weightSpecs
    const modelArtifacts = {
      modelTopology: modelData.modelTopology,
      weightsManifest: modelData.weightsManifest,
      weightSpecs: modelData.weightSpecs, // Include weight specifications
      weightData: weightBuffer,
      format: modelData.format,
      generatedBy: modelData.generatedBy,
      convertedBy: modelData.convertedBy,
      userDefinedMetadata: modelData.userDefinedMetadata,
    };

    this.model = await tf.loadLayersModel(tf.io.fromMemory(modelArtifacts));
    if (this.model) {
      this.classNamesToIndex = this.model.getUserDefinedMetadata().classNames;
      return this.classNamesToIndex;
    } else {
      return false;
    }
  }

  loadModel = async (json, weights) => {
    this.model = await tf.loadLayersModel(`${this.state.importModelApi}/${json}/${weights}/model.json`);
    if (this.model) {
      this.classNamesToIndex = this.model.getUserDefinedMetadata().classNames;
      return this.classNamesToIndex;
    } else {
      return false;
    }
  }

  updateModelMetadata = () => {
    if (this.model) {
      const classNamesToIndex = {}
      for (const sample of Object.values(this.samples)) {
        classNamesToIndex[sample.name || sample] = sample.index
      }
      this.model.setUserDefinedMetadata({ classNames: classNamesToIndex })
    }
  }
}
